/**
 * D17 — approving a `.chain` that is "Needs approval": the person confirms WHO set its target
 * and WHICH folder it points to before vouching for it (they become its author). The target is
 * shown only here — never in the listing before approval.
 *
 * Glass dialog (`ws-glass-modal`, as NewProjectDialog); the target folder is shown the way the
 * drive shows a folder — its icon and name, with its location as breadcrumbs (File Info's crumbs:
 * `filesApi.getBreadcrumbs`, display names, drive root excluded).
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Copy, ExternalLink, Link2, X } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { FileIcon, fileIconProps, miniappIconProps, useTheme, getThemeClass } from '@papan/core-ui/ui'
import { filesApi } from '../client/index.js'
import { DriveBreadcrumb } from './DriveBreadcrumb.js'

type Facts = Awaited<ReturnType<typeof filesApi.getPointerApproval>>

/** Renders the marked subjects of a (translated) sentence in the chain badge's blue (brand
 *  accent in light) — as text, never HTML. `<hl>` = highlighted; `<hlt>` = highlighted and
 *  truncated (an email — the full address is in its tooltip and the details). `icon` goes before
 *  the highlighted segment equal to `iconFor` (an app's icon before its name). */
function highlightMarked(text: string, icon?: ReactNode, iconFor?: string) {
  const out: ReactNode[] = []
  const re = /<(hlt|hl)>(.*?)<\/\1>/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const [, tag, part] = m
    out.push(tag === 'hlt'
      ? <span key={m.index} title={part} className="font-semibold file-chain-highlight inline-block max-w-[16rem] truncate align-bottom">{part}</span>
      : (
        <span key={m.index} className="font-semibold file-chain-highlight">
          {icon && part === iconFor ? <span className="inline-flex align-[-3px] mr-1">{icon}</span> : null}
          {part}
        </span>
      ))
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/**
 * The vertical chain between the shortcut and its target, with a highlight band sweeping top →
 * bottom (the direction it points). The icon is rotated 90°, so the gradient runs along its local
 * x axis. Still under prefers-reduced-motion.
 */
function ChainSweepIcon() {
  const id = `chain-sweep-${useId().replace(/:/g, '')}`
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  return (
    <>
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="-12" y1="0" x2="12" y2="0">
            <stop offset="0.2" style={{ stopColor: 'currentColor' }} className="themed-text-muted" />
            <stop offset="0.5" style={{ stopColor: 'currentColor' }} className="file-chain-sweep" />
            <stop offset="0.8" style={{ stopColor: 'currentColor' }} className="themed-text-muted" />
            {!reduced && (
              <animateTransform attributeName="gradientTransform" type="translate" from="-6 0" to="30 0" dur="1.1s" repeatCount="indefinite" />
            )}
          </linearGradient>
        </defs>
      </svg>
      <Link2 size={18} className="rotate-90 themed-text-muted" stroke={reduced ? 'currentColor' : `url(#${id})`} />
    </>
  )
}

export function ApprovePointerDialog({ fileId, fileName, rootLabel, onNavigate, appHref, onOpenApp, onCancel, onApprove }: {
  fileId: string
  fileName: string
  /** The drive root's label (as the drive's own breadcrumb shows it). */
  rootLabel: string
  /** Open a folder in the drive (null = the drive root) — a click on a crumb closes the dialog. */
  onNavigate: (folderId: string | null) => void
  /** The in-drive URL that opens an app (the requesting app's "open" link). */
  appHref?: (appId: string) => string
  /** Open an app in place (closes the dialog first). */
  onOpenApp?: (appId: string) => void
  onCancel: () => void
  onApprove: () => Promise<void>
}) {
  const { t } = useTranslation('files')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [facts, setFacts] = useState<Facts | null>(null)
  const [crumbs, setCrumbs] = useState<Array<{ id: string; name: string }> | null>(null)
  const [failed, setFailed] = useState(false)
  const [busy, setBusy] = useState(false)
  // "Requested by" opens inline to the details: the requester's email, and for a share-link
  // visitor the link's creator and the link itself (its URL only for the link's managers).
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [brief, setBrief] = useState<{ url: string; revoked: boolean; expired: boolean } | 'hidden' | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let cancelled = false
    filesApi.getPointerApproval(fileId)
      .then(async (f) => {
        if (cancelled) return
        setFacts(f)
        // The folder's location, as File Info shows it (crumbs include the folder itself).
        const c = await filesApi.getBreadcrumbs(f.target.id).catch(() => null)
        if (!cancelled) setCrumbs(c)
      })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [fileId])

  // Focus moves INTO the dialog (Cancel — the safe default) and goes back to the opener
  // on close. Escape / Enter belong to this dialog: taken in the CAPTURE phase on window,
  // before any document listener behind it (a share/file overlay's Escape) sees them.
  // Enter inside the dialog keeps its native meaning (activating the focused control);
  // an Enter from outside it is swallowed so the opener's row can't re-open it.
  const dialogRef = useRef<HTMLDivElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const keyCtx = useRef({ busy, onCancel })
  keyCtx.current = { busy, onCancel }
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    cancelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        if (!keyCtx.current.busy) keyCtx.current.onCancel()
      } else if (e.key === 'Enter') {
        e.stopPropagation()
        const inside = e.target instanceof Node && !!dialogRef.current?.contains(e.target)
        if (!inside) e.preventDefault()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      if (opener?.isConnected) opener.focus()
    }
  }, [])

  const author = facts?.author
  // The subjects (who, through what, whose link) are wrapped in `<hl>` in the locale strings.
  const authorText = !author ? ''
    : author.kind === 'user' ? `<hl>${author.name || author.email || '—'}</hl>`
    // Who asked leads when their email was recorded ("x@y.com through the app …"); else the kind.
    : author.kind === 'app' ? (author.email
      ? t('driveView.chain.approveDialog.authorAppBy', { name: author.name ?? '—', who: author.requesterName || author.email })
      : t('driveView.chain.approveDialog.authorApp', { name: author.name ?? '—' }))
    : author.kind === 'share' ? (author.email
      ? (author.linkCreatedBy
        ? t('driveView.chain.approveDialog.authorShareBy', { name: author.linkCreatedBy, who: author.requesterName || author.email })
        : t('driveView.chain.approveDialog.authorShareByUnknown', { who: author.requesterName || author.email }))
      : author.linkCreatedBy
        ? t('driveView.chain.approveDialog.authorShare', { name: author.linkCreatedBy })
        : t('driveView.chain.approveDialog.authorShareUnknown'))
    // E7 PR 1: an act that did not vouch records the person it was made for.
    : author?.kind === 'system' && author.email
      ? t('driveView.chain.approveDialog.authorSystemBy', { who: author.requesterName || author.email })
      : t('driveView.chain.approveDialog.authorSystem')
  // Every requester has a details row (an app's: the person who ran it — "not given" when
  // nobody signed in); a person's needs an email to add anything.
  const hasDetails = author?.kind === 'share' || author?.kind === 'app' || (author?.kind === 'user' && !!author.email)
  const appIcon = author?.kind === 'app' && author.appId && author.name
    ? <FileIcon name={author.name} isFolder size={16} {...miniappIconProps({ id: author.appId, name: author.name, metadataJson: author.appMetadataJson ?? null })} />
    : undefined
  const linkId = author?.kind === 'share' ? author.linkId ?? null : null
  useEffect(() => {
    if (!detailsOpen || !linkId || brief !== null) return
    let cancelled = false
    filesApi.getShareLinkBrief(linkId)
      .then((b) => { if (!cancelled) setBrief(b) })
      .catch(() => { if (!cancelled) setBrief('hidden') })
    return () => { cancelled = true }
  }, [detailsOpen, linkId, brief])
  // Location = the crumbs ABOVE the folder (the last crumb is the folder itself).
  const location = crumbs ? crumbs.slice(0, -1) : null

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[14vh] pb-4" ref={dialogRef} role="dialog" aria-modal="true" aria-label={t('driveView.chain.approveDialog.title')}>
      <div className="absolute inset-0 bg-black/50 ws-anim-fade-in" onClick={busy ? undefined : onCancel} />
      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-full max-w-md flex flex-col`}>
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-1">
          <div className="min-w-0">
            <h3 className="text-base font-semibold themed-text">{t('driveView.chain.approveDialog.title')}</h3>
            <p className="mt-1 text-xs leading-relaxed themed-text-muted">{t('driveView.chain.approveDialog.description')}</p>
          </div>
          <button onClick={onCancel} disabled={busy} className="themed-icon hover:themed-text p-1 rounded ws-glass-well shrink-0" aria-label={t('driveView.chain.approveDialog.cancel')}>
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pt-4 pb-5 space-y-3">
          {facts && (
            <section className="px-1 pb-2">
              <div className="text-[11px] uppercase tracking-wide themed-text-muted">{t('driveView.chain.approveDialog.setBy')}</div>
              {hasDetails ? (
                <button
                  type="button"
                  onClick={() => setDetailsOpen((o) => !o)}
                  aria-expanded={detailsOpen}
                  className="mt-1 -mx-1 px-1 rounded text-left text-sm themed-text break-words inline-flex items-start gap-1 hover:themed-bg-hover"
                  data-testid="chain-approve-author"
                >
                  <span>{highlightMarked(authorText, appIcon, author?.kind === 'app' ? author.name ?? undefined : undefined)}</span>
                  <ChevronDown size={14} className={`mt-[3px] shrink-0 themed-text-muted transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
                </button>
              ) : (
                <div className="mt-1 text-sm themed-text break-words" data-testid="chain-approve-author">{highlightMarked(authorText, appIcon, author?.kind === 'app' ? author.name ?? undefined : undefined)}</div>
              )}
              {hasDetails && detailsOpen && author && (
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs" data-testid="chain-approve-author-details">
                  <dt className="themed-text-muted">{t('driveView.chain.approveDialog.requester')}</dt>
                  <dd className="themed-text break-all">
                    {author.kind === 'user'
                      ? author.email
                      : author.email
                        ? <>{author.requesterName && <>{author.requesterName}<span className="themed-text-muted"> · </span></>}<span className={author.requesterName ? 'themed-text-muted' : undefined}>{author.email}</span></>
                        : <span className="themed-text-muted">{t('driveView.chain.approveDialog.requesterNoEmail')}</span>}
                  </dd>
                  {author.kind === 'app' && author.appId && author.appCanOpen && appHref && (
                    <>
                      <dt className="themed-text-muted">{t('driveView.chain.approveDialog.app')}</dt>
                      <dd className="min-w-0">
                        <a
                          href={appHref(author.appId)}
                          onClick={(e) => { if (onOpenApp && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) { e.preventDefault(); onCancel(); onOpenApp(author.appId!) } }}
                          className="inline-flex items-center gap-1.5 file-chain-highlight hover:underline"
                          data-testid="chain-approve-open-app"
                        >
                          {appIcon}
                          {t('driveView.chain.approveDialog.openApp', { name: author.name ?? '' })}
                          <ExternalLink size={11} className="shrink-0" />
                        </a>
                      </dd>
                    </>
                  )}
                  {author.kind === 'share' && (
                    <>
                      <dt className="themed-text-muted">{t('driveView.chain.approveDialog.linkCreator')}</dt>
                      <dd className="themed-text break-all">
                        {author.linkCreatedBy ?? '—'}
                        {author.linkCreatorEmail && author.linkCreatorEmail !== author.linkCreatedBy && (
                          <span className="themed-text-muted"> · {author.linkCreatorEmail}</span>
                        )}
                      </dd>
                      <dt className="themed-text-muted">{t('driveView.chain.approveDialog.link')}</dt>
                      <dd className="min-w-0">
                        {brief === null ? <span className="themed-text-muted">{t('driveView.chain.approveDialog.loading')}</span>
                          : brief === 'hidden' ? <span className="themed-text-muted">{t('driveView.chain.approveDialog.linkHidden')}</span>
                          : (
                            <span className="flex items-center gap-1.5 min-w-0">
                              <a href={brief.url} target="_blank" rel="noreferrer" className="truncate file-chain-highlight hover:underline" title={brief.url}>{brief.url}</a>
                              {(brief.revoked || brief.expired) && (
                                <span className="shrink-0 themed-text-muted">({t(brief.revoked ? 'driveView.chain.approveDialog.linkRevoked' : 'driveView.chain.approveDialog.linkExpired')})</span>
                              )}
                              <button
                                type="button"
                                className="shrink-0 p-0.5 rounded themed-icon hover:themed-text"
                                aria-label={t('driveView.chain.approveDialog.copyLink')}
                                title={copied ? t('driveView.chain.approveDialog.copied') : t('driveView.chain.approveDialog.copyLink')}
                                onClick={() => { void navigator.clipboard?.writeText(brief.url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) }) }}
                              >
                                {copied ? <Check size={12} /> : <Copy size={12} />}
                              </button>
                            </span>
                          )}
                      </dd>
                    </>
                  )}
                </dl>
              )}
            </section>
          )}

          {/* The shortcut being approved. */}
          <div className="flex items-center justify-center gap-2.5 rounded-lg ws-glass-well px-3 py-2.5">
            {/* D17: it NEEDS approval (that is why this dialog is open) — drawn uncoloured (the theme's text colour) with its badge, as in the listing. */}
            <FileIcon {...fileIconProps({ name: fileName, isFolder: facts?.target.isFolder ?? true, fileType: 'chain', chainNeedsApproval: true })} size={18} className="shrink-0" />
            <span className="text-sm font-medium themed-text truncate" title={fileName}>{fileName}</span>
          </div>

          {failed && <div className="text-sm text-red-400">{t('driveView.chain.approveDialog.loadFailed')}</div>}
          {!failed && !facts && <div className="text-sm themed-text-muted px-1">{t('driveView.chain.approveDialog.loading')}</div>}

          {facts && (
            <>
              {/* The link between the shortcut and where it points. */}
              <div className="flex justify-center" aria-hidden="true">
                <ChainSweepIcon />
              </div>
              <section className="rounded-lg ws-glass-well px-3 py-2.5">
                <div className="text-[11px] uppercase tracking-wide themed-text-muted">{t('driveView.chain.approveDialog.pointsTo')}</div>
                <div className="mt-2 flex items-center gap-3 min-w-0" data-testid="chain-approve-target">
                  <FileIcon name={facts.target.name ?? ''} isFolder={facts.target.isFolder} size={36} className="shrink-0" />
                  <div className="min-w-0 flex-1">
                    {/* The drive's own breadcrumb (compact): a long path folds into its … menu. */}
                    <div className="flex min-w-0 -ml-1">
                      {location
                        ? <DriveBreadcrumb compact breadcrumbs={location} rootLabel={rootLabel} onNavigate={(id) => { onCancel(); onNavigate(id) }} />
                        : <span className="text-xs">&nbsp;</span>}
                    </div>
                    <div className="text-sm font-semibold themed-text truncate pl-0.5">{facts.target.name ?? '—'}</div>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>

        <div className="ws-glass-footer flex items-center justify-end gap-2 px-5 py-3">
          <button ref={cancelRef} type="button" onClick={onCancel} disabled={busy} className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text rounded-lg ws-glass-well transition-colors">
            {t('driveView.chain.approveDialog.cancel')}
          </button>
          <button
            type="button"
            data-testid="chain-approve-confirm"
            disabled={busy || !facts}
            onClick={async () => { setBusy(true); try { await onApprove() } finally { setBusy(false) } }}
            className="px-4 py-1.5 text-sm themed-btn-primary rounded-lg disabled:opacity-50"
          >
            {t('driveView.chain.approveDialog.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
